import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FilePondOptions } from 'filepond';
import { FilePondComponent } from 'ngx-filepond';
import { BehaviorSubject, catchError, EMPTY } from 'rxjs';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { getApiHostUrl, getUploadResumeURL } from '../../util/const';
import { CloudUploadService } from '../../core/services/cloud-upload.service';
import {
  ResumableUploadResponse,
  StudyMaterialContentUploadSession,
  UploadSession,
} from '../../core/models/upload-session.model';
import {
  StudyMaterial,
  StudyMaterialContent,
  StudyMaterialContentResponseDTO,
} from '../../models/study-material.model';
import { ToastService } from '@black/ui';
import { ResourceService } from '../../core/state/resource/resource.service';
import {
  Resource,
  ResourceUploadStatus,
} from '../../core/state/resource/models/resource.model';
import { GoogleDrivePickerService } from '../../core/services/google-drive-picker.service';
import {
  getEffectiveResourceAccess,
  ResourceAccessResult,
} from '@black/shared';

/** Max polling delay cap in ms. */
const MAX_POLL_DELAY_MS = 60_000;
/** Max number of poll attempts per resource before giving up. */
const MAX_POLL_ATTEMPTS = 30;

@Component({
  selector: 'black-study-material-card',
  standalone: false,
  templateUrl: './study-material-card.component.html',
  styleUrl: './study-material-card.component.scss',
})
export class StudyMaterialCardComponent implements OnInit, OnDestroy {
  private fb = inject(NonNullableFormBuilder);
  private http = inject(HttpClient);
  private uploadService = inject(CloudUploadService);
  private toastService = inject(ToastService);
  private resourceService = inject(ResourceService);
  private drivePickerService = inject(GoogleDrivePickerService);

  drivePickerLoading = false;
  drivePickerError: string | null = null;

  @Input()
  studyMaterial: StudyMaterial | null = null;

  @Input()
  subscriptions: any[] = [];

  @Input()
  liveClassOwnerId?: string;

  @Input()
  isAuthorized = true;

  @Output()
  removeStudyMaterial = new EventEmitter<string>();

  @ViewChild('myPond')
  myPond!: FilePondComponent;

  get resourceAccess(): ResourceAccessResult {
    if (this.editModeEnabled) {
      return {
        hasAccess: true,
        accessType: 'INSTRUCTOR',
        effectiveSubscription: null,
        isPending: false,
        isRestrictedBySinglePass: false,
      };
    }
    if (!this.subscriptions || this.subscriptions.length === 0) {
      return {
        hasAccess: this.isAuthorized,
        accessType: this.isAuthorized ? 'FULL_COURSE' : 'NONE',
        effectiveSubscription: null,
        isPending: false,
        isRestrictedBySinglePass: false,
      };
    }
    return getEffectiveResourceAccess({
      subscriptions: this.subscriptions,
      resourceItem: {
        meetingId: this.studyMaterial?.meetingId,
        sessionDate: this.studyMaterial?.sessionDate,
      },
      liveClassOwnerId: this.liveClassOwnerId,
    });
  }

  get isAccessible(): boolean {
    return this.resourceAccess.hasAccess;
  }

  @Input()
  editModeEnabled = false;

  materialUploadForm = this.fb.group({
    resource: ['', Validators.required],
  });

  uploadingItems = new BehaviorSubject<StudyMaterialContentUploadSession[]>([]);

  uploadingItems$ = this.uploadingItems.asObservable();

  /** Tracks Google Drive / URL uploads that are still being processed server-side. */
  pendingExternalUploads = new BehaviorSubject<
    Array<{ resourceId: string; name: string; source: 'google-drive' | 'url' }>
  >([]);

  pendingExternalUploads$ = this.pendingExternalUploads.asObservable();

  currentUploadContext: null | {
    file: File;
    responseData: ResumableUploadResponse;
  } = null;

  pondOptions: FilePondOptions = {
    allowMultiple: false,
    labelIdle: 'Drop files here or Browse',
    instantUpload: false,
    allowImagePreview: true,
    allowFileTypeValidation: true,
    credits: false,
    acceptedFileTypes: [
      'image/*',
      'video/*',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
  };

  pondHandleAddFile(event: any) {
    const file = event.file.file;

    this.http
      .post<ResumableUploadResponse>(getUploadResumeURL(), {
        name: file.name,
        type: file.type,
        size: file.size,
      })
      .subscribe((data) => {
        this.materialUploadForm.controls.resource.setValue(data.resourceId);
        this.currentUploadContext = { file, responseData: data };
      });
  }

  pondHandleRemoveFile() {
    this.currentUploadContext = null;
  }

  uploadRecording(cb?: () => void) {
    if (!this.currentUploadContext?.responseData)
      throw new Error('current session context is required!');

    const { file, responseData } = this.currentUploadContext;

    this.materialUploadForm.markAllAsTouched();

    if (this.materialUploadForm.valid) {
      const formData = this.materialUploadForm.value;
      try {
        this.http
          .put<StudyMaterialContentResponseDTO>(
            getApiHostUrl() +
              `/api/v1/study-materials/${this.studyMaterial?._id}/contents`,
            {
              resourceId: formData.resource,
              contentName: file.name,
            }
          )
          .subscribe((data: StudyMaterialContentResponseDTO) => {
            const uploadSessionId = this.uploadService.addUploadFromResponse(
              file,
              responseData,
              data.resource._id,
            );

            const uploadItem: StudyMaterialContentUploadSession = {
              content: { resource: data.resource, name: file.name },
              uploadSessionId: uploadSessionId,
            };

            this.uploadingItems.next([
              ...this.uploadingItems.value,
              uploadItem,
            ]);

            // Reset the form and filepond
            this.materialUploadForm.reset();
            this.myPond.files = [];
            this.currentUploadContext = null;

            //fire callback
            if (cb) cb();
          });
      } catch (error) {
        this.toastService.showToast('Study material upload failed!', 'error');
      }
    } else {
      this.toastService.showToast('Form is invalid!', 'error');
    }
  }

  getUploadSession(uploadSessionId: number | undefined): UploadSession | null {
    if (uploadSessionId === undefined) return null;
    return this.uploadService.getUploadSession(uploadSessionId) ?? null;
  }

  removeContent(resourceId: string) {
    this.http
      .delete<StudyMaterialContentResponseDTO>(
        getApiHostUrl() +
          `/api/v1/study-materials/${this.studyMaterial?._id}/contents/${resourceId}`
      )
      .pipe(
        catchError(() => {
          this.toastService.showToast(
            'Removing study material content failed!',
            'error'
          );
          return EMPTY;
        })
      )
      .subscribe(() => {
        const itemIndex = this.studyMaterial?.contents.findIndex(
          (content) => content.resource._id == resourceId
        );

        if (itemIndex !== -1 && itemIndex !== undefined)
          this.studyMaterial?.contents.splice(itemIndex, 1);

        this.toastService.showToast(
          'Study material content removed successfully!',
          'success'
        );
      });
  }

  getIconClass(fileType: string) {
    if (fileType === 'video') {
      return 'fa-video text-red-500';
    }
    if (fileType === 'image') {
      return 'fa-image text-green-500';
    }
    return 'fa-file-alt text-blue-500';
  }

  onRemoveStudyMaterial() {
    if (this.studyMaterial) {
      this.removeStudyMaterial.emit(this.studyMaterial._id);
    } else {
      this.toastService.showToast('Failed to remove study material!', 'error');
    }
  }

  hideUnCompletedItem(item: StudyMaterialContent) {
    return !this.editModeEnabled && item.resource.uploadStatus !== 'COMPLETED';
  }

  openFile(resource: Resource) {
    if (resource.provider === 'external') {
      window.open(resource.path, '_blank', 'noopener,noreferrer');
      return;
    }
    this.resourceService.getSignedUrl(resource._id).subscribe((data) => {
      window.open(data.signedUrl, '_blank');
    });
  }

  // ---- Modal tab/sub-mode state ----

  /** Top-level tab: 'file' = Add a File, 'external-url' = Add a URL */
  activeTab: 'file' | 'external-url' = 'file';

  /** Sub-mode within the "Add a File" tab */
  fileSubMode: 'device' | 'url-download' | null = null;

  switchTab(tab: 'file' | 'external-url') {
    this.activeTab = tab;
    this.fileSubMode = null;
    this.urlSubmitError = null;
    this.externalUrlSubmitError = null;
  }

  selectFileSubMode(mode: 'device' | 'url-download') {
    this.fileSubMode = mode;
  }

  // ---- URL download upload (stores file in GCS) ----

  urlUploadForm = this.fb.group({
    contentName: ['', Validators.required],
    url: [
      '',
      [
        Validators.required,
        Validators.pattern(/^https?:\/\/[^\s/$.?#].[^\s]*$/),
      ],
    ],
    type: ['video' as string, Validators.required],
  });

  urlSubmitting = false;
  urlSubmitError: string | null = null;

  private readonly pollTimeouts = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private readonly pollAttempts = new Map<string, number>();

  submitUrlContent(cb?: () => void) {
    this.urlUploadForm.markAllAsTouched();
    if (this.urlUploadForm.invalid) return;

    this.urlSubmitting = true;
    this.urlSubmitError = null;

    const contentName = this.urlUploadForm.value.contentName ?? '';
    const url = this.urlUploadForm.value.url ?? '';
    const type = this.urlUploadForm.value.type ?? 'video';

    this.resourceService.uploadUrl({ name: contentName, url, type }).subscribe({
      next: ({ resourceId }) => {
        this.http
          .put<StudyMaterialContentResponseDTO>(
            getApiHostUrl() +
              `/api/v1/study-materials/${this.studyMaterial?._id}/contents`,
            { resourceId, contentName }
          )
          .subscribe({
            next: (data: StudyMaterialContentResponseDTO) => {
              this.urlSubmitting = false;
              if (this.studyMaterial) {
                this.studyMaterial.contents.push({
                  resource: data.resource,
                  name: data.name,
                });
              }
              this.addPendingExternalUpload(
                data.resource._id,
                contentName,
                'url'
              );
              this.startPollingForResource(data.resource._id);
              this.toastService.showToast(
                'URL queued for download!',
                'success'
              );
              this.urlUploadForm.reset({ type: 'video' });
              if (cb) cb();
            },
            error: () => {
              this.urlSubmitting = false;
              this.urlSubmitError =
                'Failed to link resource. Please try again.';
            },
          });
      },
      error: () => {
        this.urlSubmitting = false;
        this.urlSubmitError = 'Failed to submit URL. Please try again.';
      },
    });
  }

  // ---- External URL (stored as-is, opened directly in browser) ----

  externalUrlForm = this.fb.group({
    contentName: ['', Validators.required],
    url: [
      '',
      [
        Validators.required,
        Validators.pattern(/^https?:\/\/[^\s/$.?#].[^\s]*$/),
      ],
    ],
    type: ['video' as string, Validators.required],
  });

  externalUrlSubmitting = false;
  externalUrlSubmitError: string | null = null;

  submitExternalUrlContent(cb?: () => void) {
    this.externalUrlForm.markAllAsTouched();
    if (this.externalUrlForm.invalid) return;

    this.externalUrlSubmitting = true;
    this.externalUrlSubmitError = null;

    const contentName = this.externalUrlForm.value.contentName ?? '';
    const url = this.externalUrlForm.value.url ?? '';
    const type = this.externalUrlForm.value.type ?? 'video';

    this.resourceService
      .uploadExternalUrl({ name: contentName, url, type })
      .subscribe({
        next: ({ resourceId }) => {
          this.http
            .put<StudyMaterialContentResponseDTO>(
              getApiHostUrl() +
                `/api/v1/study-materials/${this.studyMaterial?._id}/contents`,
              { resourceId, contentName }
            )
            .subscribe({
              next: (data: StudyMaterialContentResponseDTO) => {
                this.externalUrlSubmitting = false;
                if (this.studyMaterial) {
                  this.studyMaterial.contents.push({
                    resource: data.resource,
                    name: data.name,
                  });
                }
                this.toastService.showToast(
                  'External URL added successfully!',
                  'success'
                );
                this.externalUrlForm.reset({ type: 'video' });
                if (cb) cb();
              },
              error: () => {
                this.externalUrlSubmitting = false;
                this.externalUrlSubmitError =
                  'Failed to link resource. Please try again.';
              },
            });
        },
        error: () => {
          this.externalUrlSubmitting = false;
          this.externalUrlSubmitError =
            'Failed to submit URL. Please try again.';
        },
      });
  }

  isResourcePolling(resourceId: string): boolean {
    return this.pollTimeouts.has(resourceId);
  }

  isPendingExternalUpload(resourceId: string): boolean {
    return this.pendingExternalUploads.value.some(
      (p) => String(p.resourceId) === String(resourceId)
    );
  }

  private startPollingForResource(resourceId: string): void {
    this.pollAttempts.set(resourceId, 0);
    this.scheduleNextPollForResource(resourceId);
  }

  private scheduleNextPollForResource(resourceId: string): void {
    const attempt = this.pollAttempts.get(resourceId) ?? 0;

    if (attempt >= MAX_POLL_ATTEMPTS) {
      this.pollTimeouts.delete(resourceId);
      this.pollAttempts.delete(resourceId);
      this.removePendingExternalUpload(resourceId);
      return;
    }

    const delay = Math.min(
      10_000 * Math.pow(2, Math.floor(attempt / 10)),
      MAX_POLL_DELAY_MS
    );
    this.pollAttempts.set(resourceId, attempt + 1);

    const existing = this.pollTimeouts.get(resourceId);
    if (existing !== undefined) clearTimeout(existing);

    const timeout = setTimeout(() => {
      this.resourceService.getById(resourceId).subscribe({
        next: (resource) => {
          if (!this.studyMaterial) return;
          const idx = this.studyMaterial.contents.findIndex(
            (c) => c.resource._id === resourceId
          );
          if (idx !== -1) {
            this.studyMaterial.contents[idx].resource = resource;
          }
          if (
            resource.uploadStatus === ResourceUploadStatus.COMPLETED ||
            resource.uploadStatus === ResourceUploadStatus.FAILED
          ) {
            this.pollTimeouts.delete(resourceId);
            this.pollAttempts.delete(resourceId);
            this.removePendingExternalUpload(resourceId);
          } else {
            this.scheduleNextPollForResource(resourceId);
          }
        },
        error: () => {
          this.scheduleNextPollForResource(resourceId);
        },
      });
    }, delay);

    this.pollTimeouts.set(resourceId, timeout);
  }

  private addPendingExternalUpload(
    resourceId: string,
    name: string,
    source: 'google-drive' | 'url'
  ): void {
    this.pendingExternalUploads.next([
      ...this.pendingExternalUploads.value,
      { resourceId, name, source },
    ]);
  }

  private removePendingExternalUpload(resourceId: string): void {
    this.pendingExternalUploads.next(
      this.pendingExternalUploads.value.filter(
        (p) => p.resourceId !== resourceId
      )
    );
  }

  private stopAllPolling(): void {
    this.pollTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.pollTimeouts.clear();
    this.pollAttempts.clear();
    this.pendingExternalUploads.next([]);
  }

  // ---- Google Drive Picker ----

  async selectFromGoogleDrive(cb?: () => void) {
    this.drivePickerLoading = true;
    this.drivePickerError = null;

    try {
      const file = await this.drivePickerService.open();
      if (!file) {
        this.drivePickerLoading = false;
        return;
      }

      this.resourceService
        .uploadGoogleDriveFile({
          fileId: file.id,
          accessToken: file.accessToken,
          name: file.name,
          mimeType: file.mimeType,
          access: 'private',
        })
        .subscribe({
          next: ({ resourceId }) => {
            this.http
              .put<StudyMaterialContentResponseDTO>(
                getApiHostUrl() +
                  `/api/v1/study-materials/${this.studyMaterial?._id}/contents`,
                { resourceId, contentName: file.name }
              )
              .subscribe({
                next: (data: StudyMaterialContentResponseDTO) => {
                  this.drivePickerLoading = false;

                  this.addPendingExternalUpload(
                    data.resource._id,
                    file.name,
                    'google-drive'
                  );

                  if (this.studyMaterial) {
                    this.studyMaterial.contents.push({
                      resource: data.resource,
                      name: data.name,
                    });
                  }
                  // Start polling so the UI updates when the background download finishes

                  this.startPollingForResource(data.resource._id);
                  this.toastService.showToast(
                    'Google Drive file queued for upload. It will be available shortly.',
                    'success'
                  );
                  if (cb) cb();
                },
                error: () => {
                  this.drivePickerLoading = false;
                  this.drivePickerError =
                    'Failed to link the Google Drive file. Please try again.';
                },
              });
          },
          error: () => {
            this.drivePickerLoading = false;
            this.drivePickerError =
              'Failed to start the Google Drive upload. Please try again.';
          },
        });
    } catch (err) {
      this.drivePickerLoading = false;
      this.drivePickerError =
        'Could not open Google Drive picker. Ensure you are signed in and popups are allowed.';
    }
  }

  ngOnInit(): void {
    this.studyMaterial?.contents.forEach((content) => {
      if (
        content.resource.uploadStatus === ResourceUploadStatus.PENDING_UPLOAD ||
        content.resource.uploadStatus === ResourceUploadStatus.UPLOADING
      ) {
        this.startPollingForResource(content.resource._id);
      }
    });
  }

  ngOnDestroy(): void {
    this.stopAllPolling();
  }
}
